import type { ReactElement } from 'react';
import { useContext, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import AuthContext from '../context/AuthContext';
import { ROUTE_REGISTRATION_SURVEY, ROUTE_SOMETHING_WENT_WRONG } from '../routes/routes.constants';
import {
  rootStore,
  useProfileState,
  useRegistrationSurveyState,
  selectProfileIsDeleted,
  selectProfileReady,
  selectRegistrationSurveyCompleted,
  selectRegistrationSurveyError,
  selectFetchRegistrationSurveyStatusErrored,
  selectFetchRegistrationSurveyStatusLoading,
  selectRegistrationSurveyNeeded,
  selectRegistrationSurveyResponseMalformed,
} from '../store';
import { FETCH_REGISTRATION_SURVEY_START } from '../store/registrationSurvey';
import { prepareBusinessUnitRedirectFromWindow } from '../utils/surveyRedirectTarget';

/**
 * Обертка над вложенными маршрутами авторизованной зоны: загрузка статуса registration-survey,
 * редирект на форму опроса, в BU при завершённом опросе или на `/error` при сбое GET.
 * Провайдер `AuthContext` уже подключён в `App`; здесь только чтение `kToken` для диспатча.
 *
 * @returns {ReactElement}
 */
export function CheckNeededFillSurveyGuard(): ReactElement {
  const { kToken } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const profileReady = useProfileState(selectProfileReady);
  const profileDeleted = useProfileState(selectProfileIsDeleted);
  const surveyLoading = useRegistrationSurveyState(selectFetchRegistrationSurveyStatusLoading);
  const surveyErrored = useRegistrationSurveyState(selectFetchRegistrationSurveyStatusErrored);
  const surveyError = useRegistrationSurveyState(selectRegistrationSurveyError);
  const surveyCompleted = useRegistrationSurveyState(selectRegistrationSurveyCompleted);
  const surveyNeeded = useRegistrationSurveyState(selectRegistrationSurveyNeeded);
  const surveyMalformed = useRegistrationSurveyState(selectRegistrationSurveyResponseMalformed);

  const businessUnitRedirectRef = useRef(false);

  useEffect(() => {
    if (!profileReady) {
      return;
    }
    if (typeof kToken !== 'string' || kToken === '') {
      return;
    }

    const { meta } = rootStore.getState().registrationSurvey;
    const alreadyRequested = meta.pending || meta.success || Object.keys(meta.errors).length > 0;
    if (alreadyRequested) {
      return;
    }

    rootStore.dispatch({ type: FETCH_REGISTRATION_SURVEY_START, payload: { token: kToken } });
  }, [profileReady, kToken]);

  useEffect(() => {
    if (!profileReady) {
      return;
    }
    if (profileDeleted === true) {
      // Редирект в passport при удалении профиля отключён (как в прежнем SurveyFlowRedirect).
      return;
    }
    if (surveyLoading) {
      return;
    }
    if (surveyErrored) {
      const errMsg = surveyError !== null ? surveyError.message : 'unknown error';
      console.error('[survey-client] GET registration-survey failed;', errMsg);
      if (location.pathname !== ROUTE_SOMETHING_WENT_WRONG) {
        navigate(
          { pathname: ROUTE_SOMETHING_WENT_WRONG, search: location.search, hash: location.hash },
          { replace: true },
        );
      }
      return;
    }
    if (surveyMalformed) {
      console.error('[survey-client] registration-survey response malformed');
      // На странице опроса не уводим на `/error`: иначе `navigate` в том же цикле, что и POST+`location.replace`, ломает выход.
      if (location.pathname === ROUTE_REGISTRATION_SURVEY) {
        return;
      }
      if (location.pathname !== ROUTE_SOMETHING_WENT_WRONG) {
        navigate(
          { pathname: ROUTE_SOMETHING_WENT_WRONG, search: location.search, hash: location.hash },
          { replace: true },
        );
      }
      return;
    }
    if (surveyCompleted && !businessUnitRedirectRef.current) {
      businessUnitRedirectRef.current = true;
      window.location.assign(prepareBusinessUnitRedirectFromWindow());
    }
  }, [
    profileReady,
    profileDeleted,
    surveyLoading,
    surveyErrored,
    surveyMalformed,
    surveyCompleted,
    surveyError,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  if (!profileReady || surveyLoading) {
    return <Outlet />;
  }

  if (
    profileReady &&
    !surveyLoading &&
    !surveyErrored &&
    !surveyMalformed &&
    !surveyCompleted &&
    surveyNeeded &&
    location.pathname !== ROUTE_REGISTRATION_SURVEY
  ) {
    return (
      <Navigate
        to={{
          pathname: ROUTE_REGISTRATION_SURVEY,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  return <Outlet />;
}
